import { propertiesService } from '@/modules/properties/api/properties.service';
import { leadsService } from '@/modules/leads/api/leads.service';
import { qrCodesService } from '@/modules/qr-codes/api/qr-codes.service';
import {
    leadToResult,
    propertyToResult,
    qrToResult,
    SEARCH_GROUP_META,
    type GlobalSearchParams,
    type SearchGroup,
} from '@/modules/search/types';

const DEFAULT_LIMIT = 5;

/**
 * Global search fans out to the existing per-entity list endpoints in
 * parallel, reusing their shared `q` free-text param. There is no unified
 * `/search` route on the API — but every list query is already tenant-scoped
 * and permission-gated in SQL, so fanning out inherits all of that for free.
 *
 * `Promise.allSettled` keeps one entity's failure (e.g. a role that can't read
 * that module) from blanking the whole panel — that group just comes back empty.
 */
export const searchService = {
    async search({ q, limit = DEFAULT_LIMIT }: GlobalSearchParams): Promise<SearchGroup[]> {
        const term = q.trim();
        if (!term) return [];

        const [properties, leads, qrCodes] = await Promise.allSettled([
            propertiesService.listPaged({ q: term, limit }),
            leadsService.listPaged({ q: term, limit }),
            qrCodesService.listPaged({ q: term, limit }),
        ]);

        const propertyResults =
            properties.status === 'fulfilled' ? properties.value.properties.map(propertyToResult) : [];
        const leadResults =
            leads.status === 'fulfilled' ? leads.value.leads.map(leadToResult) : [];
        const qrResults =
            qrCodes.status === 'fulfilled' ? qrCodes.value.codes.map(qrToResult) : [];

        // Preserve a stable section order; drop empty groups so the panel stays tight.
        return [
            { entity: 'property' as const, results: propertyResults },
            { entity: 'lead' as const,     results: leadResults },
            { entity: 'qr_code' as const,  results: qrResults },
        ]
            .filter((g) => g.results.length > 0)
            .map((g) => ({ ...g, ...SEARCH_GROUP_META[g.entity] }));
    },
};
